// Modul 40 B3 — die vier Zellen, aus denen die Bestell-Karte besteht.
//
// Leitgedanke: Die Karte ist keine Maske mit Speichern-Knopf. Jeder Wert steht als Text da
// und wird an Ort und Stelle geaendert — ein Klick macht aus dem Text ein Feld, Enter oder
// das Verlassen speichert, Escape verwirft. Alles, was schreibt, laeuft durch EINEN Hook
// (`useInlineEdit`), damit die drei Wert-Zellen nicht je eigene Sonderregeln bekommen.
import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, Plus } from "lucide-react";
import { docTypLabel, formatUsd } from "@/lib/purchase-format";
import { dokumentHerunterladen, useUploadDocument } from "@/api/purchase";
import type { PurchaseDocument } from "@/api/purchase";
import { fehlerText } from "@/lib/fehlerText";
import { parseBetrag } from "./zeilen";

const FEHLTEXT = "Speichern fehlgeschlagen";

/**
 * Der Satz des Servers zu einer Absage — oder ein Sammelsatz, wenn keiner mitkam.
 *
 * Die Regel selbst steht seit Modul 42 an EINER Stelle: `lib/fehlerText`. Diese Huelle bleibt,
 * weil die Karten-Zeilen und die Zellen sie unter diesem Namen und mit dem Sammeltext
 * `FEHLTEXT` als Vorgabe rufen. Bis Modul 42 rechnete sie selbst — und kannte dabei den
 * Platzhalter „HTTP nnn" nicht, den der Client einsetzt, wenn die Antwort zwar lesbar war,
 * aber keine Meldung enthielt (`api/client.ts:123`). Der stand dann in der Zelle, als haette
 * ihn der Server gesagt.
 */
export function serverSatz(err: unknown, sammel: string = FEHLTEXT): string {
  return fehlerText(err, sammel);
}

/**
 * Anzeige- und Download-Name eines Dokuments.
 *
 * `file_name` ist im Wire-Typ nullbar (der Agent hat die Datei angelegt, aber keinen Namen
 * uebernommen). Ohne Rueckfall hiesse der Chip "Dokument null" und die heruntergeladene
 * Datei landete ohne Endung auf der Platte — nicht mehr zu oeffnen.
 */
function dateiName(d: PurchaseDocument): string {
  return d.file_name ?? `dokument-${d.id}.pdf`;
}

/**
 * EUR mit zwei Nachkommastellen.
 *
 * Bewusst NICHT `formatEuro` aus `@/lib/dash-format`: das rundet auf ganze Euro, was fuer
 * KPI-Kacheln richtig ist, in einer AENDERBAREN Zelle aber luegen wuerde — dort stuende
 * "13 €" ueber einem gespeicherten 12,50, und beim Oeffnen spraenge die Zahl.
 */
export function formatEuroGenau(n: number | null): string {
  if (n === null) return "—";
  return `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/**
 * ISO-Datum → `TT.MM.JJJJ`, ohne Umweg ueber `Date`.
 *
 * `new Date("2026-08-18")` ist Mitternacht UTC; in einer Zeitzone westlich davon zeigte die
 * Anzeige den Vortag. Hier wird nur umgestellt, nicht gerechnet.
 */
function formatDatum(v: string | null): string {
  if (!v) return "—";
  const [j, m, t] = v.slice(0, 10).split("-");
  return t && m && j ? `${t}.${m}.${j}` : v;
}

interface ZellenBasis {
  disabled?: boolean;
  disabledGrund?: string;
  ariaLabel: string;
}

/**
 * Der gemeinsame Rumpf aller Wert-Zellen.
 *
 * `parse` liefert `undefined` fuer eine ungueltige Eingabe — das ist der Unterschied, auf
 * dem alles steht: `null` heisst "leeren" (eine Absicht), `undefined` heisst "Vertipper"
 * und darf NICHTS schreiben.
 *
 * `speichertRef` ist der Riegel gegen den Doppel-Save: Enter loest `commit()` aus, das
 * darauf folgende Blur ebenfalls — ohne Riegel gingen zwei Schreibvorgaenge derselben
 * Zelle raus. Ein Ref und keine State-Variable, weil der zweite Aufruf im selben Zug
 * kommt, bevor React neu gerendert hat.
 */
function useInlineEdit<T>(p: {
  value: T;
  onSave: (v: T) => Promise<unknown>;
  parse: (roh: string) => T | undefined;
  format: (v: T) => string;
  ungueltigText: string;
  disabled?: boolean;
}) {
  const [offen, setOffen] = useState(false);
  const [entwurf, setEntwurf] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const speichertRef = useRef(false);

  function oeffnen() {
    if (p.disabled) return;
    setEntwurf(p.format(p.value));
    setFehler(null);
    setOffen(true);
  }

  function schliessen() {
    // Waehrend gespeichert wird, schliesst nichts: sonst klappt die Zelle bei Escape zu,
    // der Request laeuft aber weiter — ein Fehler bliebe unsichtbar, ein Erfolg liesse den
    // Wert spaeter aus dem Nichts umspringen (Befund beider Reviewer).
    if (speichertRef.current) return;
    setOffen(false);
    setFehler(null);
  }

  async function commit() {
    if (speichertRef.current) return;
    const neu = p.parse(entwurf);
    if (neu === undefined) {
      setFehler(p.ungueltigText);
      return;
    }
    if (neu === p.value) {
      schliessen();
      return;
    }
    speichertRef.current = true;
    setBusy(true);
    setFehler(null);
    try {
      await p.onSave(neu);
      setOffen(false);
    } catch (err) {
      setFehler(serverSatz(err));
    } finally {
      speichertRef.current = false;
      setBusy(false);
    }
  }

  return { offen, entwurf, setEntwurf, fehler, busy, oeffnen, schliessen, commit };
}

/** Ruhezustand: der Wert als Knopf. Gepunkteter Unterstrich = "hier kann man tippen". */
function Anzeige(
  p: ZellenBasis & { text: string; rechts?: boolean; mehrzeilig?: boolean; onClick: () => void },
) {
  return (
    <button
      type="button"
      aria-label={p.ariaLabel}
      aria-disabled={p.disabled ? true : undefined}
      title={p.disabled ? p.disabledGrund : undefined}
      onClick={p.onClick}
      className={[
        "rounded px-1 text-left text-xs",
        p.rechts ? "text-right tabular-nums" : "",
        p.mehrzeilig ? "whitespace-pre-wrap" : "",   // Umbrueche der Notiz bleiben stehen
        p.disabled
          ? "cursor-not-allowed text-ink-2"
          : "cursor-text underline decoration-dotted decoration-ink-2 underline-offset-4 hover:bg-background focus-visible:outline-2",
      ].join(" ")}
    >
      {p.text}
    </button>
  );
}

function Meldung({ text }: { text: string }) {
  return <span role="alert" className="ml-1 text-xs text-destructive">{text}</span>;
}

export function InlineBetrag(
  p: ZellenBasis & {
    value: number | null;
    waehrung: "USD" | "EUR";
    onSave: (v: number | null) => Promise<unknown>;
  },
) {
  const e = useInlineEdit<number | null>({
    value: p.value,
    onSave: p.onSave,
    parse: parseBetrag,
    // Im Feld steht die deutsche Schreibweise, die `parseBetrag` auch wieder annimmt.
    format: (v) => (v === null ? "" : String(v).replace(".", ",")),
    ungueltigText: "Ungültiger Betrag",
    disabled: p.disabled,
  });

  if (!e.offen) {
    const text = p.waehrung === "USD" ? formatUsd(p.value) : formatEuroGenau(p.value);
    return <Anzeige {...p} text={text} rechts onClick={e.oeffnen} />;
  }
  return (
    <span className="inline-flex items-center">
      <Input
        autoFocus
        inputMode="decimal"
        aria-label={p.ariaLabel}
        aria-busy={e.busy}
        readOnly={e.busy}
        value={e.entwurf}
        onChange={(ev) => e.setEntwurf(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter") { ev.preventDefault(); void e.commit(); }
          if (ev.key === "Escape") e.schliessen();
        }}
        onBlur={() => void e.commit()}
        className="h-7 w-28 text-right text-xs tabular-nums"
      />
      {e.fehler ? <Meldung text={e.fehler} /> : null}
    </span>
  );
}

export function InlineDatum(
  p: ZellenBasis & { value: string | null; onSave: (v: string | null) => Promise<unknown> },
) {
  const e = useInlineEdit<string | null>({
    value: p.value,
    onSave: p.onSave,
    // Ein Datumsfeld liefert entweder `YYYY-MM-DD` oder den leeren String; ungueltig kann es
    // hier nicht werden, deshalb gibt es kein `undefined`.
    parse: (roh) => (roh === "" ? null : roh),
    format: (v) => (v ? v.slice(0, 10) : ""),
    ungueltigText: "Ungültiges Datum",
    disabled: p.disabled,
  });

  if (!e.offen) return <Anzeige {...p} text={formatDatum(p.value)} onClick={e.oeffnen} />;
  return (
    <span className="inline-flex items-center">
      <Input
        autoFocus
        type="date"
        aria-label={p.ariaLabel}
        aria-busy={e.busy}
        readOnly={e.busy}
        value={e.entwurf}
        onChange={(ev) => e.setEntwurf(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter") { ev.preventDefault(); void e.commit(); }
          if (ev.key === "Escape") e.schliessen();
        }}
        onBlur={() => void e.commit()}
        className="h-7 w-36 text-xs"
      />
      {e.fehler ? <Meldung text={e.fehler} /> : null}
    </span>
  );
}

export function InlineText(
  p: ZellenBasis & {
    value: string | null;
    onSave: (v: string | null) => Promise<unknown>;
    mehrzeilig?: boolean;
  },
) {
  const e = useInlineEdit<string | null>({
    value: p.value,
    onSave: p.onSave,
    // Leer heisst `null`, nicht `""` — sonst stuende in der Datenbank ein leerer String da,
    // wo "keine Notiz" gemeint ist, und jede Abfrage auf `IS NULL` liefe daran vorbei.
    parse: (roh) => (roh.trim() === "" ? null : roh.trim()),
    format: (v) => v ?? "",
    ungueltigText: "Ungültige Eingabe",
    disabled: p.disabled,
  });

  if (!e.offen) {
    return <Anzeige {...p} text={p.value ?? "—"} mehrzeilig={p.mehrzeilig} onClick={e.oeffnen} />;
  }
  // `mehrzeilig` hiess bis hierhin nur "Enter beendet nicht" — gerendert wurde trotzdem ein
  // einzeiliges Feld, in dem gar kein Umbruch entstehen konnte (Befund Reviewer 1).
  const gemeinsam = {
    autoFocus: true,
    "aria-label": p.ariaLabel,
    "aria-busy": e.busy,
    readOnly: e.busy,
    value: e.entwurf,
    onChange: (ev: { target: { value: string } }) => e.setEntwurf(ev.target.value),
    onBlur: () => void e.commit(),
  };
  return (
    <span className="inline-flex items-center">
      {p.mehrzeilig ? (
        <Textarea
          {...gemeinsam}
          rows={3}
          onKeyDown={(ev) => { if (ev.key === "Escape") e.schliessen(); }}
          className="w-full text-xs"
        />
      ) : (
        <Input
          {...gemeinsam}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") { ev.preventDefault(); void e.commit(); }
            if (ev.key === "Escape") e.schliessen();
          }}
          className="h-7 w-64 text-xs"
        />
      )}
      {e.fehler ? <Meldung text={e.fehler} /> : null}
    </span>
  );
}

/**
 * Die Datei-Zelle einer Zeile: vorhandene Dokumente als Chips, daneben "+ Datei".
 *
 * `shipmentId` wird beim Upload MITGESENDET, wenn die Zelle an einem Container steht —
 * ohne sie haengt die Datei nur an der Bestellung und erschiene an jedem Container.
 */
/**
 * Ein Dokument als anklickbarer Chip.
 *
 * Eigener Baustein, weil ihn zwei Stellen brauchen: die Datei-Zelle (mit Hochladen) und die
 * Zeile "Sonstige" (nur Ansehen) — dort soll nichts hochgeladen werden koennen, aber die
 * vorhandenen Dateien muessen erreichbar sein.
 */
export function DokumentChip(p: { doc: PurchaseDocument; onFehler?: (t: string) => void }) {
  const [eigenerFehler, setEigenerFehler] = useState<string | null>(null);
  const melden = p.onFehler ?? setEigenerFehler;
  return (
    <>
      <button
        type="button"
        aria-label={`Dokument ${dateiName(p.doc)} herunterladen`}
        title={dateiName(p.doc)}
        onClick={() => {
          // apiFetchBlob liest seit dem Download-Fix den Fehler-Body (ack: 0, error: [{ message }])
          // und wirft den echten Fehlertext des Servers aus (oder Fallback 'Download fehlgeschlagen').
          void dokumentHerunterladen(p.doc.id, dateiName(p.doc))
            .catch((err) => melden(serverSatz(err, "Download fehlgeschlagen")));
        }}
        className="inline-flex max-w-48 items-center gap-1 truncate rounded border border-line px-1.5 py-0.5 text-xs text-ink-2 hover:bg-background"
      >
        <Paperclip className="size-3 shrink-0" />
        {docTypLabel(p.doc.typ ?? "sonstig")}
      </button>
      {eigenerFehler ? <Meldung text={eigenerFehler} /> : null}
    </>
  );
}

export function DateiZelle(p: {
  orderId: number;
  shipmentId: number | null;
  docs: PurchaseDocument[];
  ariaLabel: string;
  disabled?: boolean;
}) {
  const upload = useUploadDocument(p.orderId);
  const feldRef = useRef<HTMLInputElement | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  async function hochladen(datei: File) {
    setFehler(null);
    try {
      const result = await upload.mutateAsync({ file: datei, shipmentId: p.shipmentId ?? undefined });
      if (result.outcome === "duplicate") {
        setFehler("Dokument ist bereits vorhanden — es wurde nicht erneut angelegt");
      }
    } catch (err) {
      // `apiUpload` wertet denselben Umschlag aus wie `apiFetch` und wirft die Meldung des
      // Servers mit (api/client.ts:184) — z. B. „Nur PDF-Dateien sind erlaubt". Bis Modul 42
      // band dieser Zweig den Fehler gar nicht erst und zeigte den festen Satz; der Einkauf
      // sah dann nicht, dass schlicht das Dateiformat nicht passte.
      setFehler(serverSatz(err, "Upload fehlgeschlagen"));
    }
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {p.docs.map((d) => <DokumentChip key={d.id} doc={d} onFehler={setFehler} />)}
      <input
        ref={feldRef}
        type="file"
        accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        // Der Name gehoert an den KNOPF: ein verstecktes Feld liest kein Screenreader vor,
        // und der sichtbare Knopf hiess bis hierhin ueberall nur "Datei" (Befund beider
        // Reviewer — auf einer Karte stehen bis zu fuenf davon).
        aria-hidden
        tabIndex={-1}
        className="hidden"
        onChange={(ev) => {
          const datei = ev.target.files?.[0];
          if (datei) void hochladen(datei);
          ev.target.value = "";
        }}
      />
      <button
        type="button"
        aria-label={`${p.ariaLabel} PDF/Excel`}
        disabled={p.disabled || upload.isPending}
        onClick={() => feldRef.current?.click()}
        className="inline-flex items-center gap-0.5 rounded px-1 text-xs text-ink-2 underline decoration-dotted underline-offset-4 hover:bg-background disabled:opacity-50"
      >
        <Plus className="size-3" />
        {upload.isPending ? "lädt …" : "PDF/Excel"}
      </button>
      {fehler ? <Meldung text={fehler} /> : null}
    </span>
  );
}
